// saft_reader.js
//
// Reads a SAF-T Financial file in the browser and reduces it to the handful of numbers a price
// depends on: how many vouchers the company books, how many invoices it sends and receives, how
// many bills it pays, and whether it runs payroll. Nothing is kept beyond those counts, and
// nothing is sent anywhere.
//
// The file is streamed rather than handed to DOMParser, because a busy company's SAF-T file runs
// to hundreds of megabytes and a DOM of it would take the tab down with it. We look for the few
// elements we care about -- Header, Account, Customer, Supplier, AnalysisTypeTableEntry and
// Transaction -- none of which ever contains another of its own kind, so the next closing tag of
// the same name always ends it.
//
// Which header fields mean what, and the quirks of each vendor, are taken from the accounting
// app's importer (Saft::Adapter::Header and Saft::Import::AuditFile).

import { isZip, xmlEntries } from './zip_reader'

export class SaftError extends Error {}

const ELEMENTS = /<([\w.-]+:)?(Header|Account|Customer|Supplier|AnalysisTypeTableEntry|Transaction)[\s>]/g

// The first text of a child element, whatever namespace prefix the vendor used. Visma writes
// `<n1:...>`, Uni Micro `<nl:...>` and Fiken none at all.
const patterns = new Map()
function text(xml, name) {
  let pattern = patterns.get(name)
  if (!pattern) {
    pattern = new RegExp(`<(?:[\\w.-]+:)?${name}>([^<]*)<`)
    patterns.set(name, pattern)
  }
  const match = pattern.exec(xml)
  return match ? decodeEntities(match[1].trim()) : ''
}

// Named entities and numeric references alike: an exporter that does not write UTF-8 escapes
// "Blåbær AS" as "Bl&#229;b&#230;r AS".
function decodeEntities(value) {
  if (!value.includes('&')) return value
  return value
    .replace(/&#(x[0-9a-f]+|\d+);/gi, (reference, code) => {
      const point = code[0].toLowerCase() === 'x' ? parseInt(code.slice(1), 16) : parseInt(code, 10)
      return point <= 0x10ffff ? String.fromCodePoint(point) : reference
    })
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

function each(xml, name, callback) {
  const pattern = new RegExp(`<(?:[\\w.-]+:)?${name}>([\\s\\S]*?)</(?:[\\w.-]+:)?${name}>`, 'g')
  let match
  while ((match = pattern.exec(xml))) callback(match[1])
}

// The leading four digits of an account, so 1920, 19200 and "1920:10001" all read as 1920.
function accountNumber(accountId) {
  const digits = /^\d{4}/.exec(accountId)
  return digits ? parseInt(digits[0], 10) : null
}

const isBank = (number) => number >= 1920 && number <= 1999
const isPayroll = (number) => number >= 5000 && number <= 5099
const isRevenue = (number) => number >= 3000 && number <= 3999
const isInvestment = (number) => number >= 1300 && number <= 1399
const isCost = (number) => number >= 4000 && number <= 8999
const isVat = (number) => number >= 2700 && number <= 2799

// A Norwegian organisation number: nine digits starting with 8 or 9. A customer that has one is
// a business, and a business is who an invoice gets sent to as EHF.
function organisationNumber(value) {
  const match = /(?:^|\D)([89]\d{8})(?:\D|$)/.exec(value.replace(/\s/g, ''))
  return match ? match[1] : null
}

const sum = (lines) => lines.reduce((total, line) => total + line.amount, 0)

function parseDate(value) {
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? new Date(`${value.slice(0, 10)}T00:00:00Z`) : null
}

// A pattern that finds any of the names as words in an account line or a supplier's name.
export function namePattern(names) {
  const escaped = names.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  return new RegExp(`(^|[^\\p{L}])(${escaped.join('|')})([^\\p{L}]|$)`, 'iu')
}

// The key the system that wrote the file is billed under when it is none of the vendors we know.
export const SOFTWARE_BILLER = 'software'

const LEGAL_FORMS = /\b(AS|ASA|AB|A\/S|ApS|Oy|Ltd|Limited|GmbH|Inc|BV|Norge|Norway)\b\.?/giu

// What the system that wrote the file bills as, taken from the header: "Xledger AS" is billed as
// Xledger. SoftwareID only stands in when there is no company name, since it is as often a
// product or a version as a name. Too short a name would find everything, so it finds nothing.
function softwareBiller(header) {
  const written = header.softwareCompanyName || header.softwareId || ''
  const name = written
    .replace(LEGAL_FORMS, ' ')
    .replace(/[\s,.-]+/g, ' ')
    .trim()
  return name.length >= 3 ? { key: SOFTWARE_BILLER, pattern: namePattern([name]) } : null
}

export class SaftScanner {
  // `billers` is a list of { key, pattern }: the accounting systems whose own invoices we look
  // for in the books, so we can say what the customer actually paid them. The system named in
  // the file's header is looked for too, after them, so a vendor we have no data on is found
  // as well.
  constructor({ billers = [] } = {}) {
    this.billers = [...billers]
    this.billerSuppliers = new Map()
    this.billed = {}
    this.buffer = ''
    this.header = {}
    this.accounts = new Map()
    this.businessPartners = new Set()
    this.employeeAnalysisTypes = new Set()
    this.employees = new Set()
    this.payrollCost = 0
    this.payrollMonths = new Set()
    this.firstDate = null
    this.lastDate = null
    this.counts = {
      transactions: 0,
      salesInvoices: 0,
      salesInvoicesToBusinesses: 0,
      purchaseInvoices: 0,
      purchaseInvoicesFromBusinesses: 0,
      supplierPayments: 0,
      payrollTransactions: 0,
      bankTransactions: 0,
      revenueTransactions: 0,
    }
    this.sawAuditFile = false
  }

  feed(chunk) {
    this.buffer += chunk
    if (!this.sawAuditFile && /<([\w.-]+:)?AuditFile[\s>]/.test(this.buffer)) this.sawAuditFile = true

    ELEMENTS.lastIndex = 0
    let consumed = 0
    let match
    while ((match = ELEMENTS.exec(this.buffer))) {
      const [opening, prefix = '', name] = match
      const close = `</${prefix}${name}>`
      const end = this.buffer.indexOf(close, match.index + opening.length)
      if (end < 0) {
        consumed = match.index
        break
      }
      this.element(name, this.buffer.slice(match.index + opening.length, end))
      consumed = end + close.length
      ELEMENTS.lastIndex = consumed
    }
    if (!match) {
      // Nothing left open: keep only a tail long enough to hold a tag split across two chunks.
      consumed = Math.max(consumed, this.buffer.length - 64)
    }
    this.buffer = this.buffer.slice(consumed)
  }

  element(name, xml) {
    switch (name) {
      case 'Header':
        return this.readHeader(xml)
      case 'Account':
        return this.readAccount(xml)
      case 'Customer':
        return this.readPartner(xml, 'CustomerID', 'C')
      case 'Supplier':
        return this.readPartner(xml, 'SupplierID', 'S')
      case 'AnalysisTypeTableEntry':
        return this.readAnalysisType(xml)
      case 'Transaction':
        return this.readTransaction(xml)
    }
  }

  // Saft::Adapter::Header, field for field.
  readHeader(xml) {
    const company = /<(?:[\w.-]+:)?Company>([\s\S]*?)<\/(?:[\w.-]+:)?Company>/.exec(xml)?.[1] || ''
    this.header = {
      softwareCompanyName: text(xml, 'SoftwareCompanyName'),
      softwareId: text(xml, 'SoftwareID'),
      createdOn: parseDate(text(xml, 'AuditFileDateCreated')),
      companyName: text(company, 'Name'),
      organisationNumber: organisationNumber(text(company, 'RegistrationNumber')),
      selectionStart: this.selectionStart(xml),
      selectionEnd: this.selectionEnd(xml),
    }
    const software = softwareBiller(this.header)
    if (software) this.billers.push(software)
  }

  selectionStart(xml) {
    const date = parseDate(text(xml, 'SelectionStartDate'))
    if (date) return date

    let period = parseInt(text(xml, 'PeriodStart'), 10)
    const year = parseInt(text(xml, 'PeriodStartYear'), 10)
    if (!year || isNaN(period)) return null
    // Xledger numbers its first period 0, against the specification.
    if (period === 0) period = 1
    return new Date(Date.UTC(year, period - 1, 1))
  }

  selectionEnd(xml) {
    const date = parseDate(text(xml, 'SelectionEndDate'))
    if (date) return date

    const period = parseInt(text(xml, 'PeriodEnd'), 10)
    const year = parseInt(text(xml, 'PeriodEndYear'), 10)
    if (!year || isNaN(period)) return null
    // Some systems call the year-end adjustments period 13; to us that is still December.
    return new Date(Date.UTC(year, Math.min(period, 12), 0))
  }

  readAccount(xml) {
    const id = text(xml, 'AccountID')
    const number = accountNumber(id)
    if (number === null) return

    const balance =
      ['OpeningDebitBalance', 'OpeningCreditBalance', 'ClosingDebitBalance', 'ClosingCreditBalance']
        .map((name) => Math.abs(parseFloat(text(xml, name)) || 0))
        .reduce((sum, value) => sum + value, 0) > 0

    this.accounts.set(id, { number, balance })
    // Fiken books each employee's owed salary to a sub-account of its own, 2930:xxxxx. The
    // importer turns those into employees (SubAccount::SalaryEmployee), and so do we.
    if (/^2930:/.test(id)) this.employees.add(`account:${id}`)
  }

  readPartner(xml, idField, kind) {
    const id = text(xml, idField)
    if (!id) return
    const number =
      organisationNumber(text(xml, 'RegistrationNumber')) || organisationNumber(text(xml, 'TaxRegistrationNumber'))
    if (number) this.businessPartners.add(`${kind}:${id}`)

    if (kind === 'S') {
      const biller = this.billerNamed(text(xml, 'Name'))
      if (biller) this.billerSuppliers.set(id, biller)
    }
  }

  billerNamed(name) {
    return name ? this.billers.find((biller) => biller.pattern.test(name))?.key : undefined
  }

  // Uni Micro marks payroll lines with an "Ansatte" analysis; the description is the only thing
  // that says which of the file's own analysis types that is.
  readAnalysisType(xml) {
    if (/ansatt|arbeidstaker|employee/i.test(text(xml, 'AnalysisTypeDescription'))) {
      this.employeeAnalysisTypes.add(text(xml, 'AnalysisType'))
    }
  }

  readTransaction(xml) {
    this.counts.transactions++

    const date = parseDate(text(xml, 'TransactionDate'))
    if (date) {
      if (!this.firstDate || date < this.firstDate) this.firstDate = date
      if (!this.lastDate || date > this.lastDate) this.lastDate = date
    }

    let bank = false
    let payroll = false
    let revenue = false
    let customerDebit = null
    let supplierCredit = null
    let supplierDebit = false
    let supplierBiller = null
    const costLines = []
    let vat = false
    each(xml, 'Line', (line) => {
      const number = accountNumber(text(line, 'AccountID'))
      const debit = /<(?:[\w.-]+:)?DebitAmount>/.test(line)
      const customer = text(line, 'CustomerID')
      const supplier = text(line, 'SupplierID')

      if (this.billers.length > 0) {
        supplierBiller ||= this.billerSuppliers.get(supplier)
        if (isCost(number)) {
          const amount = (debit ? 1 : -1) * (parseFloat(text(line, 'Amount')) || 0)
          costLines.push({ amount, biller: this.billerNamed(text(line, 'Description')) })
        }
        if (isVat(number)) vat = true
      }

      if (isBank(number)) bank = true
      if (isRevenue(number)) revenue = true
      if (isPayroll(number)) {
        payroll = true
        this.payrollCost += (debit ? 1 : -1) * (parseFloat(text(line, 'Amount')) || 0)
        if (this.employeeAnalysisTypes.size > 0) {
          each(line, 'Analysis', (analysis) => {
            if (this.employeeAnalysisTypes.has(text(analysis, 'AnalysisType'))) {
              this.employees.add(`analysis:${text(analysis, 'AnalysisID')}`)
            }
          })
        }
      }
      if (customer && debit) customerDebit = customer
      if (supplier && !debit) supplierCredit = supplier
      if (supplier && debit) supplierDebit = true
    })

    if (bank) this.counts.bankTransactions++
    if (payroll) {
      this.counts.payrollTransactions++
      if (date) this.payrollMonths.add(date.toISOString().slice(0, 7))
    }
    if (revenue) this.counts.revenueTransactions++

    // An invoice is a receivable or payable raised without money moving; the same partner line
    // next to a bank line is a payment of one.
    if (customerDebit && !bank) {
      this.counts.salesInvoices++
      if (this.businessPartners.has(`C:${customerDebit}`)) this.counts.salesInvoicesToBusinesses++
    }
    if (supplierCredit && !bank) {
      this.counts.purchaseInvoices++
      if (this.businessPartners.has(`S:${supplierCredit}`)) this.counts.purchaseInvoicesFromBusinesses++
    }
    if (supplierDebit && bank) this.counts.supplierPayments++
    if (costLines.length > 0) this.billFrom(text(xml, 'Description'), supplierBiller, costLines, vat, date)
  }

  // What the accounting system itself charged, as the cost lines it can be held to -- a credit
  // note counts against it. Only an invoice *from* the vendor is theirs in full. Otherwise a
  // voucher may settle several things at once (a card statement, a batch of bank lines), so we
  // count just the lines whose own text names the vendor, and trust the voucher's text only when
  // there is a single cost line it can be talking about.
  billFrom(description, supplierBiller, costLines, vat, date) {
    if (supplierBiller) {
      this.bill(supplierBiller, sum(costLines), vat, date)
      return
    }

    const named = costLines.filter((line) => line.biller)
    if (named.length > 0) {
      for (const biller of new Set(named.map((line) => line.biller))) {
        this.bill(biller, sum(named.filter((line) => line.biller === biller)), vat, date)
      }
      return
    }

    const biller = this.billerNamed(description)
    if (biller && costLines.length === 1) this.bill(biller, costLines[0].amount, vat, date)
  }

  // Our prices are ex. VAT, so a voucher booked without a VAT line (a company that is not VAT
  // registered) has its 25 % taken off. The months they billed in say whether they bill monthly,
  // and whether they did it for the whole period or only since the customer moved to them.
  bill(biller, cost, vat, date) {
    if (cost === 0) return
    const billed = (this.billed[biller] ||= { amount: 0, vouchers: 0, firstDate: null, months: [] })
    billed.amount += vat ? cost : cost / 1.25
    billed.vouchers++
    if (!date) return
    if (!billed.firstDate || date < billed.firstDate) billed.firstDate = date
    const month = date.toISOString().slice(0, 7)
    if (!billed.months.includes(month)) billed.months.push(month)
  }

  // The period the file speaks for, in months. The header's selection is what the customer
  // asked to export, but an export of the current year often selects all twelve months, so the
  // selection stops at the day the file was made.
  months() {
    let start = this.header.selectionStart || this.firstDate
    let end = this.header.selectionEnd || this.lastDate
    if (this.header.createdOn && end && this.header.createdOn < end) end = this.header.createdOn
    if (!start || !end || end < start) return null

    const days = (end - start) / 86400000 + 1
    return Math.max(1, Math.round((days / 30.4375) * 10) / 10)
  }

  summary() {
    if (!this.sawAuditFile || (!this.header.softwareId && !this.header.softwareCompanyName)) {
      throw new SaftError(
        'Dette ser ikke ut som en SAF-T-fil. Last opp XML-filen du eksporterte fra regnskapsprogrammet.',
      )
    }

    const balances = [...this.accounts.values()]
    const months = this.months()
    return {
      ...this.header,
      months,
      firstDate: this.firstDate,
      lastDate: this.lastDate,
      ...this.counts,
      employees: this.employees.size,
      payrollCost: this.payrollCost,
      payrollMonths: this.payrollMonths.size,
      hasShareCapital: balances.some((account) => account.number === 2000 && account.balance),
      hasInvestments: balances.some((account) => isInvestment(account.number) && account.balance),
      billed: this.billed,
    }
  }
}

// One SAF-T file -- a Blob of XML -- read through a scanner.
async function scan(stream, onBytes, options) {
  const scanner = new SaftScanner(options)
  const counter = new TransformStream({
    transform(chunk, controller) {
      onBytes(chunk.byteLength)
      controller.enqueue(chunk)
    },
  })
  const reader = stream.pipeThrough(counter).pipeThrough(new TextDecoderStream()).getReader()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    scanner.feed(value)
  }
  return scanner.summary()
}

// Reads an uploaded SAF-T file, or a zip of them, and reports progress as a fraction.
//
// A Fiken complete export holds one file per fiscal year. We price on the most recent year that
// is complete, because the current year is usually only a few months in, and fall back to the
// most recent one when no year is.
export async function readSaft(file, { onProgress = () => {}, billers = [] } = {}) {
  if (!isZip(file)) {
    let read = 0
    return scan(file.stream(), (n) => onProgress(Math.min(1, (read += n) / file.size)), { billers })
  }

  const entries = await xmlEntries(file)
  if (entries.length === 0) throw new SaftError('Zip-filen inneholder ingen XML-fil.')

  const total = entries.reduce((sum, entry) => sum + entry.size, 0) || 1
  let read = 0
  const summaries = []
  for (const entry of entries) {
    try {
      summaries.push(await scan(await entry.stream(), (n) => onProgress(Math.min(1, (read += n) / total)), { billers }))
    } catch (error) {
      if (!(error instanceof SaftError)) throw error
    }
  }
  if (summaries.length === 0) throw new SaftError('Fant ingen SAF-T-fil i zip-filen.')

  const usable = summaries.filter((summary) => summary.transactions > 0 && summary.months)
  const candidates = usable.length > 0 ? usable : summaries
  const byEnd = (a, b) => (b.selectionEnd || b.lastDate || 0) - (a.selectionEnd || a.lastDate || 0)
  const complete = candidates.filter((summary) => summary.months >= 11.5).sort(byEnd)
  return complete[0] || candidates.sort(byEnd)[0]
}
